/**
 * DrumLanding — the drum feature's front door.
 *
 * Structured to match the piano landing page (hero with stats, featured grid,
 * how-it-works, community, input reference) so the two halves of the product
 * feel like one app, but built from the drum feature's own data and its own
 * sub-components. Nothing here is imported from `HomePage`.
 *
 * Mounted by hand in `App.tsx`, alongside the soundboard, so either can hold
 * the `/african-drums` route, with the soundboard at `/drum-soundboard`.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AFRICAN_INSTRUMENTS } from '../../../data/africanInstruments';
import { useInView, usePageLoadAnimation } from '../../../hooks/useAnimation';
import { useAuth } from '../../../context/AuthContext';
import {
  CHALLENGES_PER_INSTRUMENT,
  TOTAL_CHALLENGES,
  getChallenges,
} from '../data/challenges';
import { PERFECT_WINDOW_MS } from '../lib/scoring';

export const DrumLanding: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoaded = usePageLoadAnimation();

  const [statsRef, statsInView] = useInView<HTMLDivElement>();
  const [gridRef, gridInView] = useInView<HTMLDivElement>();
  const [stepsRef, stepsInView] = useInView<HTMLDivElement>();

  return (
    <div className="min-h-screen overflow-hidden">
      {/* ------------------------------------------------------------ hero */}
      <section className="relative min-h-[85vh] flex items-center">
        <div className="absolute inset-0 bg-studio-950">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(245,166,35,0.08)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(224,122,95,0.06)_0%,transparent_50%)]" />
        </div>

        <div className="relative w-full px-4 sm:px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left: copy */}
              <div
                className={`order-2 lg:order-1 transition-all duration-700 text-center lg:text-left ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-6 lg:mb-8">
                  <div className="w-12 h-[2px] bg-amber-500 hidden lg:block" />
                  <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
                    Traditional Percussion
                  </span>
                </div>

                <h1
                  className="font-display text-[clamp(2.5rem,10vw,8rem)] font-bold text-stone-100 leading-[0.9] mb-6 lg:mb-8"
                  style={{
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 150ms',
                  }}
                >
                  Play the
                  <br />
                  <span className="text-amber-500 italic">drums.</span>
                </h1>

                <p
                  className="text-stone-400 text-lg lg:text-xl leading-relaxed mb-8 lg:mb-10 max-w-md mx-auto lg:mx-0"
                  style={{
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 250ms',
                  }}
                >
                  Twelve instruments from across Africa, each with ten rhythm
                  challenges. Listen to the phrase, watch it fall, then play it
                  back in time.
                </p>

                <div
                  className="flex flex-wrap items-center gap-4 justify-center lg:justify-start"
                  style={{
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 350ms',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => navigate('/drum-challenges')}
                    className="btn-primary"
                  >
                    Start Playing
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/drum-soundboard')}
                    className="btn-secondary"
                  >
                    Explore Sounds
                  </button>
                </div>

                <div
                  ref={statsRef}
                  className={`flex flex-wrap justify-center lg:justify-start gap-8 lg:gap-12 mt-12 lg:mt-16 pt-6 lg:pt-8 border-t border-studio-800 transition-all duration-700 ${
                    statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  <DrumStat
                    value={AFRICAN_INSTRUMENTS.length}
                    label="Instruments"
                    inView={statsInView}
                    delay={0}
                  />
                  <DrumStat
                    value={TOTAL_CHALLENGES}
                    label="Challenges"
                    inView={statsInView}
                    delay={100}
                  />
                  <DrumStat
                    value={PERFECT_WINDOW_MS}
                    suffix="ms"
                    label="Perfect Window"
                    inView={statsInView}
                    delay={200}
                  />
                </div>
              </div>

              {/* Right: drum-head visual */}
              <DrumHeroVisual isLoaded={isLoaded} />
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- instrument grid */}
      <section ref={gridRef} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div
            className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 transition-all duration-700 ${
              gridInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div>
              <span className="text-coral-400 text-sm font-semibold tracking-widest uppercase mb-3 block">
                The Collection
              </span>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-stone-100">
                Twelve <span className="italic text-stone-400">drums.</span>
              </h2>
            </div>

            <Link
              to="/drum-challenges"
              className="touch-target group flex items-center gap-2 text-amber-500 hover:text-amber-400 font-semibold transition-colors"
            >
              View All Challenges
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {AFRICAN_INSTRUMENTS.map((instrument, index) => (
              <Link
                key={instrument.slug}
                to="/drum-challenges"
                className="group flex flex-col bg-studio-800 border border-studio-700 rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:border-amber-500/50 transition-all duration-300"
                style={{
                  opacity: gridInView ? 1 : 0,
                  transform: gridInView ? 'translateY(0)' : 'translateY(24px)',
                  transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${
                    100 + (index % 3) * 100
                  }ms`,
                }}
              >
                <div className="aspect-[4/3] bg-studio-850 overflow-hidden">
                  <img
                    src={instrument.image}
                    alt={`${instrument.nameEn} — ${instrument.nameZh}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="flex flex-col flex-1 p-6">
                  <h3 className="font-display text-2xl font-bold text-stone-100">
                    {instrument.nameEn}
                  </h3>
                  <p className="text-amber-500 text-sm font-semibold mt-0.5">
                    {instrument.nameZh}
                  </p>
                  <p className="text-stone-500 text-xs font-mono mt-3">
                    {instrument.originEn}
                  </p>
                  <p className="text-stone-400 text-sm leading-relaxed mt-4 flex-1">
                    {instrument.descriptionEn}
                  </p>

                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-studio-700">
                    <span className="text-stone-500 text-xs font-mono">
                      {instrument.pads.length} voices ·{' '}
                      {getChallenges(instrument.slug).length} challenges
                    </span>
                    <span className="text-amber-500 text-xs font-semibold transition-transform group-hover:translate-x-1">
                      Play →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ how it works */}
      <section
        ref={stepsRef}
        className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 bg-studio-950 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-500/5 to-transparent" />

        <div className="max-w-7xl mx-auto relative">
          <div
            className={`text-center mb-12 lg:mb-20 transition-all duration-700 ${
              stepsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-4 block">
              How It Works
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100">
              Call and <span className="italic text-amber-500">response.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-12">
            {[
              {
                num: '01',
                title: 'Listen',
                desc: 'The drum plays the phrase to you while the notes fall down their lanes. Every voice has its own colour and its own key.',
                color: '#81b29a',
              },
              {
                num: '02',
                title: 'Play It Back',
                desc: `A four-beat count-in, then it is your turn. Strike each pad as its note crosses the line — within ${PERFECT_WINDOW_MS}ms for a perfect hit.`,
                color: '#f5a623',
              },
              {
                num: '03',
                title: 'Climb the Board',
                desc: `Ten challenges per instrument, ${CHALLENGES_PER_INSTRUMENT * AFRICAN_INSTRUMENTS.length} in all. Sign in and every run is ranked against everyone else's.`,
                color: '#e07a5f',
              },
            ].map((step, i) => (
              <div
                key={step.num}
                className="relative group"
                style={{
                  opacity: stepsInView ? 1 : 0,
                  transform: stepsInView ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${
                    200 + i * 150
                  }ms`,
                }}
              >
                <span
                  className="font-display text-[8rem] font-bold leading-none absolute -top-8 -left-4 opacity-10 transition-opacity duration-500 group-hover:opacity-20"
                  style={{ color: step.color }}
                >
                  {step.num}
                </span>
                <div className="relative pt-16">
                  <div
                    className="w-16 h-1 mb-6 transition-all duration-300 group-hover:w-24"
                    style={{ backgroundColor: step.color }}
                  />
                  <h3 className="text-2xl font-bold text-stone-100 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-stone-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- leaderboard */}
      <DrumCommunitySection signedIn={!!user} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components — the drum feature's own, not shared with the piano landing.
// ---------------------------------------------------------------------------

interface DrumStatProps {
  value: number;
  suffix?: string;
  label: string;
  inView: boolean;
  delay: number;
}

/** A stat that counts up once it scrolls into view. */
function DrumStat({ value, suffix = '', label, inView, delay }: DrumStatProps) {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!inView) return undefined;

    const timer = window.setTimeout(() => {
      const durationMs = 1000;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const progress = Math.min((currentTime - startTime) / durationMs, 1);
        // Ease-out quart: fast at first, settling onto the final number.
        const eased = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(eased * value));

        if (progress < 1) requestAnimationFrame(animate);
        else setCount(value);
      };

      requestAnimationFrame(animate);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [inView, value, delay]);

  return (
    <div>
      <p className="font-display text-4xl font-bold text-stone-100">
        {count}
        {suffix}
      </p>
      <p className="text-stone-500 text-sm mt-1">{label}</p>
    </div>
  );
}

/** A drum head seen from above, with lugs around the rim. */
function DrumHeroVisual({ isLoaded }: { isLoaded: boolean }) {
  return (
    <div
      className="order-1 lg:order-2 flex justify-center lg:justify-end"
      style={{
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? 'scale(1)' : 'scale(0.9)',
        transition: 'all 0.9s cubic-bezier(0.22, 1, 0.36, 1) 200ms',
      }}
    >
      <div className="relative">
        <div className="absolute -inset-20 opacity-20">
          <div
            className="w-full h-full rounded-full border border-amber-500/30 animate-pulse"
            style={{ animationDuration: '4s' }}
          />
        </div>
        <div className="absolute -inset-32 opacity-10">
          <div className="w-full h-full rounded-full border border-coral-400/30" />
        </div>

        <div className="relative w-72 h-72 lg:w-96 lg:h-96">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            {/* Tension lugs around the rim */}
            {[...Array(10)].map((_, i) => (
              <rect
                key={`lug-${i}`}
                x="96"
                y="8"
                width="8"
                height="16"
                rx="3"
                fill="#e07a5f"
                opacity={0.5 + (i % 3) * 0.2}
                transform={`rotate(${i * 36} 100 100)`}
              />
            ))}

            {/* Shell and head */}
            <circle cx="100" cy="100" r="76" fill="none" stroke="#d4a574" strokeWidth="3" opacity="0.5" />
            <circle cx="100" cy="100" r="68" fill="#111111" stroke="#f5a623" strokeWidth="2" />

            {/* Concentric ripples, as if the head were struck */}
            {[52, 36, 20].map((r, i) => (
              <circle
                key={`ripple-${r}`}
                cx="100"
                cy="100"
                r={r}
                fill="none"
                stroke="#f5a623"
                strokeWidth="1"
                opacity={0.15 + i * 0.1}
              />
            ))}

            <circle cx="100" cy="100" r="9" fill="#f5a623" className="animate-pulse-subtle" />
          </svg>
        </div>
      </div>
    </div>
  );
}

/** Closing section: what the leaderboard is for, and how to get on it. */
function DrumCommunitySection({ signedIn }: { signedIn: boolean }) {
  const [ref, isInView] = useInView<HTMLElement>();
  const navigate = useNavigate();

  const milestones = [
    { label: 'First Clean Phrase', desc: 'Finish any challenge without a single miss.', icon: '◎', color: '#81b29a' },
    { label: 'Full Combo', desc: 'Chain every stroke in a phrase without breaking.', icon: '⬡', color: '#f5a623' },
    { label: 'S-Rank Run', desc: 'Land 100% accuracy on any challenge.', icon: '✦', color: '#e8b86d' },
    { label: 'Master Phrase', desc: 'Clear challenge ten on any instrument.', icon: '◈', color: '#e07a5f' },
  ];

  return (
    <section
      ref={ref}
      className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 bg-studio-950 relative overflow-hidden"
    >
      <div
        className="absolute -right-32 top-0 bottom-0 w-96 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(129,178,154,0.04), rgba(129,178,154,0.01))',
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateX(0)' : 'translateX(-24px)',
              transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <span
              className="text-sm font-semibold tracking-widest uppercase mb-4 block"
              style={{ color: '#81b29a' }}
            >
              Progress &amp; Community
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100 mb-6">
              Chase the{' '}
              <span className="italic" style={{ color: '#81b29a' }}>
                leaderboard.
              </span>
            </h2>
            <p className="text-stone-400 leading-relaxed mb-8 max-w-md">
              Every challenge keeps its own ranking. Accuracy, best combo and
              grade are saved for each run, so you can see where a phrase is
              costing you and come back for it.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/drum-leaderboard')}
                className="btn-primary"
                style={{ background: '#81b29a', color: '#0a0a0b' }}
              >
                View Leaderboard
              </button>
              {!signedIn && (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="btn-secondary"
                >
                  Sign Up Free
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {milestones.map((milestone, i) => (
              <div
                key={milestone.label}
                className="group rounded-2xl p-5 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, #111 0%, #0d0d0d 100%)',
                  border: `1px solid ${milestone.color}20`,
                  opacity: isInView ? 1 : 0,
                  transform: isInView
                    ? 'translateY(0) scale(1)'
                    : 'translateY(20px) scale(0.96)',
                  transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${
                    150 + i * 100
                  }ms`,
                }}
              >
                <div
                  className="absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"
                  style={{ background: milestone.color, filter: 'blur(24px)' }}
                />

                <span
                  className="text-2xl block mb-3 transition-transform duration-300 group-hover:scale-125 origin-left"
                  style={{ color: milestone.color }}
                >
                  {milestone.icon}
                </span>
                <p className="text-stone-100 font-semibold text-sm mb-1">
                  {milestone.label}
                </p>
                <p className="text-stone-600 text-xs leading-relaxed">
                  {milestone.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
